import { Input, Select } from '@/components/ui';
import { isoDate } from '@/lib/format';

/** The ranges a business actually asks for, so the common case is one click. */
export const DATE_PRESETS: Record<string, { label: string; from: () => Date; to: () => Date }> = {
  today: { label: 'Today', from: () => new Date(), to: () => new Date() },
  week: {
    label: 'Last 7 days',
    from: () => new Date(Date.now() - 6 * 86_400_000),
    to: () => new Date(),
  },
  month: {
    label: 'Last 30 days',
    from: () => new Date(Date.now() - 29 * 86_400_000),
    to: () => new Date(),
  },
  thisMonth: {
    label: 'This month',
    from: () => new Date(new Date().getFullYear(), new Date().getMonth(), 1),
    to: () => new Date(),
  },
};

export interface DateRange {
  from: string;
  to: string;
  preset: string;
}

/** Everything unfiltered, which is where most lists should start. */
export const ALL_TIME: DateRange = { from: '', to: '', preset: 'all' };

export function rangeFromPreset(preset: string): DateRange {
  const range = DATE_PRESETS[preset];
  if (!range) return ALL_TIME;
  return { preset, from: isoDate(range.from()), to: isoDate(range.to()) };
}

/**
 * The earliest date an export will claim to cover.
 *
 * A report has to name the period it covers, and "all time" is not a period.
 * This is before any record in the system can exist, so the export is both
 * complete and honest about what it looked at.
 */
export const EARLIEST_EXPORT_DATE = '2000-01-01';

/** A screen filter as dates an export can state on its front page. */
export function exportRangeOf(range: DateRange): { from: string; to: string; label: string } {
  if (range.from && range.to) {
    return { from: range.from, to: range.to, label: `${range.from} to ${range.to}` };
  }
  const today = isoDate(new Date());
  return { from: EARLIEST_EXPORT_DATE, to: today, label: `everything up to ${today}` };
}

/**
 * A period picker shared by every money screen.
 *
 * One component rather than six, because six date filters drift into six
 * different ideas of what "this month" means.
 */
export function DateRangeFilter({
  value,
  onChange,
  label = 'Period',
}: {
  value: DateRange;
  onChange: (range: DateRange) => void;
  label?: string;
}) {
  return (
    <>
      <div className="w-full sm:w-40">
        <Select
          label={label}
          value={value.preset}
          onChange={(event) =>
            onChange(
              event.target.value === 'all' ? ALL_TIME : rangeFromPreset(event.target.value),
            )
          }
        >
          <option value="all">All time</option>
          {Object.entries(DATE_PRESETS).map(([key, preset]) => (
            <option key={key} value={key}>{preset.label}</option>
          ))}
          <option value="custom">Custom</option>
        </Select>
      </div>
      <div className="w-full sm:w-40">
        <Input
          label="From"
          type="date"
          value={value.from}
          max={value.to || undefined}
          onChange={(event) => onChange({ ...value, from: event.target.value, preset: 'custom' })}
        />
      </div>
      <div className="w-full sm:w-40">
        <Input
          label="To"
          type="date"
          value={value.to}
          min={value.from || undefined}
          onChange={(event) => onChange({ ...value, to: event.target.value, preset: 'custom' })}
        />
      </div>
    </>
  );
}
